import {verifyLanStaff,localMobileStaff} from './lan-staff';
import {diagnosticFetch} from './sync-diagnostics';
export interface SmmStaffDirectoryItem{
  readonly staffId:string;
  readonly loginId?:string;
  readonly displayName:string;
  readonly role:string;
}

export interface SmmStaffSession extends SmmStaffDirectoryItem{
  readonly sessionToken:string;
  readonly expiresAt?:string;
}

const KEY='mfk.smm.staff-session.v2';
const LEGACY_KEY='mfk.smm.staff-session.v1';

function cleanSession(value:unknown):SmmStaffSession|null{
  if(!value||typeof value!=='object'||Array.isArray(value))return null;
  const row=value as Record<string,unknown>;
  const staffId=String(row.staffId??'').trim();
  const loginId=String(row.loginId??'').trim();
  const displayName=String(row.displayName??'').trim();
  const role=String(row.role??'').trim();
  const sessionToken=String(row.sessionToken??'').trim();
  const expiresAt=typeof row.expiresAt==='string'?row.expiresAt:undefined;
  if(!staffId||!displayName||sessionToken.length<32)return null;
  return Object.freeze({staffId,...(loginId?{loginId}:{}),displayName,role,sessionToken,...(expiresAt?{expiresAt}:{})});
}

export function readSmmStaffSession():SmmStaffSession|null{
  try{
    localStorage.removeItem(LEGACY_KEY);
    const raw=localStorage.getItem(KEY);
    return raw?cleanSession(JSON.parse(raw)):null;
  }catch{return null;}
}

export function saveSmmStaffSession(value:SmmStaffSession){
  localStorage.setItem(KEY,JSON.stringify(value));
}

/** Forget an explicitly rejected local session without issuing another command. */
export function forgetSmmStaffSession(){
  try{localStorage.removeItem(KEY);}catch{}
}

export function clearSmmStaffSession(){
  try{
    const session=readSmmStaffSession();
    forgetSmmStaffSession();
    if(session){
      void fetch('/api/smm/staff/session',{
        method:'POST',
        headers:{'x-mfk-smm-session':session.sessionToken},
      }).catch(()=>{});
    }
  }catch{}
}

/** Admin sync authorization also fails during backend outages; only this origin owns session validity. */
export async function isSmmStaffSessionExplicitlyInvalid(session:SmmStaffSession):Promise<boolean>{
  if(session.sessionToken.startsWith('LAN.'))return false;
  try{
    const response=await diagnosticFetch('session','/api/smm/staff/session',{
      method:'GET',cache:'no-store',headers:{accept:'application/json','x-mfk-smm-session':session.sessionToken},
    });
    const body=await response.json().catch(()=>({})) as Record<string,unknown>;
    return response.status===401&&body.code==='SMM_SESSION_UNAUTHORIZED';
  }catch{return false;}
}

export async function refreshSmmStaffSession():Promise<SmmStaffSession|null>{
  const current=readSmmStaffSession();
  if(!current)return null;
  if(current.sessionToken.startsWith('LAN.'))return current;
  try{
    const response=await diagnosticFetch('session','/api/smm/staff/session',{
      method:'GET',
      cache:'no-store',
      headers:{'x-mfk-smm-session':current.sessionToken},
    });
    const body=await response.json().catch(()=>({})) as Record<string,unknown>;
    // A slower refresh must never restore a logged-out session or change a newer actor.
    const latest=readSmmStaffSession();
    if(latest?.sessionToken!==current.sessionToken)return latest;
    if(response.status===401&&body.code==='SMM_SESSION_UNAUTHORIZED'){
      forgetSmmStaffSession();
      return null;
    }
    // Transport/service errors do not establish that the trusted session is invalid.
    if(!response.ok)return current;
    const next=cleanSession({
      staffId:body.staffId,
      loginId:body.loginId??current.loginId,
      displayName:body.displayName,
      role:body.role,
      sessionToken:current.sessionToken,
      expiresAt:body.expiresAt??current.expiresAt,
    });
    if(!next||next.staffId!==current.staffId)return current;
    saveSmmStaffSession(next);
    return next;
  }catch{
    // Keep the last trusted device session during temporary network failure.
    return readSmmStaffSession();
  }
}

export async function listSmmStaff():Promise<readonly SmmStaffDirectoryItem[]>{
  const session=readSmmStaffSession();
  if(!session)return Object.freeze([]);
  const local=await localMobileStaff();if(local)return local.filter(s=>s.active&&s.role!=='VIEWER').map(s=>({staffId:s.staffId,loginId:s.loginId,displayName:s.name,role:s.role}));
  const response=await fetch('/api/smm/staff',{cache:'no-store',headers:{accept:'application/json','x-mfk-smm-session':session.sessionToken}});
  const body=await response.json().catch(()=>({})) as Record<string,unknown>;
  if(!response.ok)throw new Error(String(body.code||'SMM_STAFF_DIRECTORY_FAILED'));
  const staff=Array.isArray(body.staff)?body.staff:[];
  return Object.freeze(staff.flatMap(raw=>{
    if(!raw||typeof raw!=='object'||Array.isArray(raw))return[];
    const row=raw as Record<string,unknown>;
    const staffId=String(row.staffId??'').trim();
    const loginId=String(row.loginId??'').trim();
    const displayName=String(row.displayName??'').trim();
    const role=String(row.role??'').trim();
    return staffId&&displayName?[Object.freeze({staffId,...(loginId?{loginId}:{}),displayName,role})]:[];
  }));
}

export async function verifySmmStaff(identifier:string,pin:string):Promise<SmmStaffSession>{
  const cleanPin=String(pin||'').replace(/\D/g,'');
  if(cleanPin.length<4||cleanPin.length>8)throw new Error('PIN 必須為 4–8 位數字');

  const local=await verifyLanStaff(identifier,cleanPin);if(local){saveSmmStaffSession(local);return local;}
  const challengeResponse=await fetch('/api/smm/staff/challenge',{
    method:'POST',
    headers:{'content-type':'application/json'},
    body:JSON.stringify({loginId:identifier}),
  });
  const challengeBody=await challengeResponse.json().catch(()=>({})) as Record<string,unknown>;
  if(!challengeResponse.ok){
    throw new Error(String(challengeBody.message||challengeBody.code||'未能開始員工驗證')+' · HTTP '+challengeResponse.status);
  }

  const challengeId=String(challengeBody.challengeId??'').trim();
  const canonicalStaffId=String(challengeBody.staffId??'').trim();
  const nonce=String(challengeBody.nonce??'').trim();
  if(!challengeId||!canonicalStaffId||!nonce||challengeBody.authProtocol!=='MFK_SERVER_PIN_V2')throw Error('SMM_AUTH_CHALLENGE_INVALID');
  const response=await fetch('/api/smm/staff/verify',{
    method:'POST',
    headers:{'content-type':'application/json'},
    body:JSON.stringify({staffId:canonicalStaffId,challengeId,pin:cleanPin}),
  });
  const raw=await response.text();
  let body:Record<string,unknown>={};
  try{body=raw?JSON.parse(raw) as Record<string,unknown>:{};}catch{}
  if(!response.ok){
    const code=String(body.code||'SMM_STAFF_VERIFY_HTTP_'+response.status);
    const message=String(body.message||'員工帳戶驗證失敗');
    throw new Error(message+' · '+code+' · HTTP '+response.status);
  }

  const session=cleanSession({
    staffId:body.staffId,
    loginId:body.loginId,
    displayName:body.displayName,
    role:body.role,
    sessionToken:body.sessionToken,
    expiresAt:body.expiresAt,
  });
  if(!session)throw new Error('SMM_STAFF_SESSION_INVALID');
  saveSmmStaffSession(session);
  return session;
}
